import { Injectable } from '@angular/core';
import { CanActivate, Router, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { Store, select } from '@ngrx/store';
import { AppState } from 'src/app/services/models/application-details.model';
import { selectAppList, selectCurrentUserRole } from 'src/app/store/app-state.selectors';
import { AppUserType, UserAppResponse } from 'src/app/services/models/app-user.model';
import { AppUserService } from 'src/app/services/app-user.service';

/**
 * Restricts the admin section to Owner and Admin.
 *
 * The role is derived from `appList` joined against `selectedApplication`, so it
 * needs the app list loaded. On a cold load — a bookmark or an external redirect
 * straight into an admin URL — the store is empty and nothing else fetches it,
 * which would read the role as null and bounce the user to home. So load it here
 * when it's missing.
 *
 * Fetching is safe in this guard, unlike BoardAccessGuard: it sits below
 * MsalGuard in the route tree, so authentication has already resolved and the
 * request carries a token. Every path still emits exactly one value — a guard
 * that completes empty stalls the router silently.
 */
@Injectable({
  providedIn: 'root'
})
export class AdminGuard implements CanActivate {

  constructor(
    private store: Store<AppState>,
    private appUserService: AppUserService,
    private router: Router
  ) {}

  canActivate(): Observable<boolean | UrlTree> {
    return this.store.pipe(
      select(selectAppList),
      take(1),
      switchMap(appList => {
        if (appList && appList.length > 0) {
          return of(appList);
        }

        // getUserAppList() dispatches setAppList, so the role selector below
        // sees the loaded list. On failure fall through with an empty list and
        // let the role check deny access.
        return this.appUserService.getUserAppList().pipe(
          catchError(error => {
            console.error('AdminGuard: could not load the user app list', error);
            return of([] as UserAppResponse[]);
          })
        );
      }),
      switchMap(() => this.store.pipe(select(selectCurrentUserRole), take(1))),
      map(userRole => {
        // Only allow Admin and Owner roles to access admin routes
        const hasAdminAccess = userRole === AppUserType.Admin || userRole === AppUserType.Owner;

        if (!hasAdminAccess) {
          console.warn('Access denied to admin routes. User role:', userRole);
          // Send them to the board home rather than blocking with a blank screen
          return this.router.parseUrl('/dashboard/home');
        }

        return true;
      })
    );
  }
}
